import Input, { InputProps } from "@mui/joy/Input";
import { TimePicker, TimePickerProps } from "@mui/x-date-pickers";
import {
  unstable_useDateField as useDateField,
  UseDateFieldProps,
} from "@mui/x-date-pickers/DateField";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { useClearableField } from "@mui/x-date-pickers/hooks";
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection,
} from "@mui/x-date-pickers/models";
import * as React from "react";

interface JoyFieldProps extends InputProps {
  label?: React.ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
  enableAccessibleFieldDOMStructure?: boolean;
  InputProps?: {
    ref?: React.Ref<HTMLDivElement>;
    endAdornment?: React.ReactNode;
    startAdornment?: React.ReactNode;
  };
  formControlSx?: InputProps["sx"];
}

type JoyFieldComponent = ((
  props: JoyFieldProps & React.RefAttributes<HTMLDivElement>,
) => React.JSX.Element) & { displayName: string };

const JoyField = React.forwardRef((props: JoyFieldProps, ref: React.Ref<HTMLDivElement>) => {
  const {
    // Should be ignored
    enableAccessibleFieldDOMStructure: _,
    disabled,
    InputProps: { ref: containerRef, startAdornment, endAdornment } = {},
    endDecorator,
    startDecorator,
    slotProps,
    inputRef,
    label,
    ...other
  } = props;

  return (
    <Input
      ref={ref}
      disabled={disabled}
      startDecorator={
        startAdornment || startDecorator ? (
          <React.Fragment>
            {startAdornment}
            {startDecorator}
          </React.Fragment>
        ) : null
      }
      endDecorator={
        endAdornment || endDecorator ? (
          <React.Fragment>
            {endAdornment}
            {endDecorator}
          </React.Fragment>
        ) : null
      }
      slotProps={{
        ...slotProps,
        root: { ...slotProps?.root, ref: containerRef },
        input: {
          ...slotProps?.input,
          ref: inputRef,
          placeholder: typeof label === "string" ? label : undefined,
        },
      }}
      {...other}
    />
  );
}) as JoyFieldComponent;
JoyField.displayName = "JoyField";

interface JoyDateFieldProps
  extends
    UseDateFieldProps<Date, false>,
    BaseSingleInputFieldProps<Date | null, Date, FieldSection, false, DateValidationError> {}

export const JoyDateField = React.forwardRef(
  (props: JoyDateFieldProps, ref: React.Ref<HTMLDivElement>) => {
    const { slots, slotProps, ...textFieldProps } = props;

    const fieldResponse = useDateField<Date, false, typeof textFieldProps>({
      ...textFieldProps,
      enableAccessibleFieldDOMStructure: false,
    });

    /* If you don't need a clear button, you can skip the use of this hook */
    // MUI X and Joy depend on different major versions of @mui/system, so
    // their sx types don't match even though the values are compatible.
    const processedFieldProps = useClearableField({
      ...fieldResponse,
      slots,
      slotProps,
    } as Parameters<typeof useClearableField>[0]);

    return <JoyField ref={ref} {...processedFieldProps} />;
  },
);
JoyDateField.displayName = "JoyDateField";

/**
 * Controls a picker so it opens when its field takes focus or is clicked, as well as from its
 * button.
 *
 * Two of the picker's own closes have to be ignored. Closing hands focus back to the field, so a
 * focus straight after a close mustn't reopen it. And the open picker treats a click on its field
 * as a click outside, so a close during a press on the field is ignored. Tabbing out of the field
 * closes the picker, which otherwise stays open behind the next field's.
 */
const useOpenOnFocus = ({
  onOpen,
  onClose,
  fieldOnFocus,
  fieldOnMouseDown,
  fieldOnKeyDown,
}: {
  onOpen?: () => void;
  onClose?: () => void;
  fieldOnFocus?: React.FocusEventHandler;
  fieldOnMouseDown?: React.MouseEventHandler;
  fieldOnKeyDown?: React.KeyboardEventHandler;
}) => {
  const [open, setOpen] = React.useState(false);
  const closedAt = React.useRef(0);
  const pressingField = React.useRef(false);

  const openPicker = () => {
    setOpen(true);
    onOpen?.();
  };

  const closePicker = () => {
    closedAt.current = Date.now();
    setOpen(false);
    onClose?.();
  };

  return {
    open,
    onOpen: openPicker,
    onClose: () => {
      if (!pressingField.current) {
        closePicker();
      }
    },
    onFieldKeyDown: (event: React.KeyboardEvent) => {
      fieldOnKeyDown?.(event);
      if (event.key === "Tab" && open) {
        closePicker();
      }
    },
    onFieldFocus: (event: React.FocusEvent) => {
      fieldOnFocus?.(event);
      // Long enough to cover the focus handed back on close, short enough not to swallow a
      // deliberate refocus.
      if (Date.now() - closedAt.current > 200) {
        openPicker();
      }
    },
    onFieldMouseDown: (event: React.MouseEvent) => {
      fieldOnMouseDown?.(event);
      pressingField.current = true;
      // The click that closes the picker comes after the mouseup, in the same task, so the press
      // ends once that's been handled.
      document.addEventListener(
        "mouseup",
        () => setTimeout(() => (pressingField.current = false)),
        { once: true },
      );
      openPicker();
    },
  };
};

// MUI X and Joy depend on different major versions of @mui/system, so the field's slot props are
// typed loosely.
// oxlint-disable-next-line typescript/no-explicit-any
type LooseFieldProps = Record<string, any> | undefined;

export const JoyDatePicker = React.forwardRef(
  (props: DatePickerProps<Date, false>, ref: React.Ref<HTMLDivElement>) => {
    const fieldProps = props.slotProps?.field as LooseFieldProps;
    const { open, onOpen, onClose, onFieldFocus, onFieldMouseDown, onFieldKeyDown } =
      useOpenOnFocus({
        onOpen: props.onOpen,
        onClose: props.onClose,
        fieldOnFocus: fieldProps?.onFocus,
        fieldOnMouseDown: fieldProps?.onMouseDown,
        fieldOnKeyDown: fieldProps?.onKeyDown,
      });
    return (
      <DatePicker
        ref={ref}
        {...props}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        slots={{ ...props.slots, field: JoyDateField }}
        slotProps={{
          ...props.slotProps,
          field: {
            ...fieldProps,
            onFocus: onFieldFocus,
            onMouseDown: onFieldMouseDown,
            onKeyDown: onFieldKeyDown,
            formControlSx: {
              flexDirection: "row",
            },
            // oxlint-disable-next-line typescript/no-explicit-any
          } as any,
        }}
      />
    );
  },
);
JoyDatePicker.displayName = "JoyDatePicker";

export const JoyTimePicker = React.forwardRef(
  (props: TimePickerProps<Date, false>, ref: React.Ref<HTMLDivElement>) => {
    const fieldProps = props.slotProps?.field as LooseFieldProps;
    const { open, onOpen, onClose, onFieldFocus, onFieldMouseDown, onFieldKeyDown } =
      useOpenOnFocus({
        onOpen: props.onOpen,
        onClose: props.onClose,
        fieldOnFocus: fieldProps?.onFocus,
        fieldOnMouseDown: fieldProps?.onMouseDown,
        fieldOnKeyDown: fieldProps?.onKeyDown,
      });
    return (
      <TimePicker
        ref={ref}
        {...props}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        slots={{ ...props.slots, field: JoyDateField }}
        slotProps={{
          ...props.slotProps,
          field: {
            ...fieldProps,
            onFocus: onFieldFocus,
            onMouseDown: onFieldMouseDown,
            onKeyDown: onFieldKeyDown,
            formControlSx: {
              flexDirection: "row",
            },
            // oxlint-disable-next-line typescript/no-explicit-any
          } as any,
        }}
      />
    );
  },
);
JoyTimePicker.displayName = "JoyTimePicker";

export default JoyDatePicker;
